import React, { ChangeEvent, useEffect, useState } from 'react'

import { Status } from '../../../components/cards/statusTag'
import { useModalControl } from '../../../hooks/useModalControl'
import {
  useEditSubCategoryMutation,
  useGetSingleSubCategoryQuery,
} from '../../../redux-toolkits/products/products.slice'
import { Product } from '../../../redux-toolkits/products/products.type'
import { ImageBanner } from '../../../components/images/imageBanner'

import { Button } from '../../../components/button'
import { BackButton } from '../../../components/button/backButton'
import Icon from '../../../components/icon'
import { AddSubCategoryModal } from '../../../components/modal/app_modals/addSubCategoryModal'
import { ImageUploadPreviewModal } from '../../../components/modal/app_modals/ImagePreviewModal'
import { LoadingOval } from '../../../components/spinner/Spinner'
import { HeaderSubheader } from '../../../components/typography/HeaderSubheader'
import { PRIMARY_COLOR } from '../../../constants'
import { SubCategoryCuratedProducts } from './subCategoryCuratedProducts'
import { SubCategoryFeaturedBundle } from './subCategoryFeaturedBundle'
import { SubCategoryProducts } from './subCategoryProducts'

export const SingleSubCategoryView: React.FC<{
  subCategoryId: string
}> = ({ subCategoryId }) => {
  const [productPicks, setProductPicks] = useState<Product[]>([])
  const [subcategoryProducts, setSubcategoryProducts] = useState<Product[]>([])
  const [open, setOpen] = useState<'curated' | 'products'>('curated')

  const [uploadedIcon, setUploadedIcon] = useState<File>()

  const { modal, modalOpen, setModalOpen, handleModalOpen, handleModalClose } =
    useModalControl()

  const [editSubCategory] = useEditSubCategoryMutation()

  const {
    data: singleSubCategoryDetails,
    isLoading: fetchingSingleSubCategoryDetails,
    refetch: refetchSubCategoryDetails,
  } = useGetSingleSubCategoryQuery({
    subcategoryId: subCategoryId,
    isAdmin: true,
  })

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return

    const selectedFile = event.target.files[0]
    setUploadedIcon(selectedFile)
    handleModalOpen('imageUploadPreviewModal')
  }

  useEffect(() => {
    setProductPicks(singleSubCategoryDetails?.data?.ourPicks ?? [])
  }, [singleSubCategoryDetails])

  return fetchingSingleSubCategoryDetails ? (
    <div className="w-full flex items-center justify-center">
      <LoadingOval color={PRIMARY_COLOR} />
    </div>
  ) : (
    <div className="flex flex-col gap-6">
      <BackButton text={`${singleSubCategoryDetails?.data?.category?.name}`} />

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-2 flex-1 min-w-fit max-w-[600px]">
          <ImageBanner
            imageUrl={singleSubCategoryDetails?.data?.icon ?? ''}
            loading={false}
            className="
                    !rounded-full
                    !w-[80px] !max-h-[80px]
                    !bg-contain
                    "
            errors={''}
            onImageEdit={(e) => {
              handleFileChange(e)
            }}
          />

          <p className="text-2xl font-recoleta font-semibold">
            {singleSubCategoryDetails?.data?.name}
          </p>

          {singleSubCategoryDetails?.data?.description ? (
            <p className="text-sec-black text-base">
              {singleSubCategoryDetails?.data?.description}
            </p>
          ) : undefined}

          <Status
            text={
              singleSubCategoryDetails?.data?.isActive ? 'Active' : 'Inactive'
            }
            type={singleSubCategoryDetails?.data?.isActive ? 'success' : 'fail'}
          />
        </div>

        <Button
          type="button"
          onClick={() => {
            handleModalOpen('editSubCategoryModal')
          }}
          primary
          className="w-fit"
          label="Manage subcategory"
          size="medium"
        />
      </div>

      <SubCategoryFeaturedBundle
        subCategoryId={subCategoryId}
        bundle={singleSubCategoryDetails?.data?.featuredBundle}
      />

      {/* Curated Products  */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-4 ">
          <HeaderSubheader
            title="Curated Products"
            titleClassName="!font-bold"
          />

          <button
            type="button"
            className="w-fit"
            onClick={() => {
              setOpen('curated')
            }}
          >
            <Icon
              id={'left-caret'}
              width={16}
              height={16}
              className={`${
                open === 'curated' ? 'rotate-90' : '-rotate-90'
              } text-sec-black transition-all duration-300`}
            />
          </button>
        </div>

        {open === 'curated' && (
          <SubCategoryCuratedProducts
            productPicks={productPicks}
            singleSubCategoryDetails={singleSubCategoryDetails}
            subCategoryId={subCategoryId}
            refetchSubCategoryDetails={() => {
              refetchSubCategoryDetails()
            }}
            setProductPicks={setProductPicks}
            fetchingSingleSubCategoryDetails={fetchingSingleSubCategoryDetails}
          />
        )}
      </div>

      {/* Subcategory Products  */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-4 ">
          <HeaderSubheader
            title="Subcategory products"
            titleClassName="!font-bold"
          />

          <button
            type="button"
            className="w-fit"
            onClick={() => {
              setOpen('products')
            }}
          >
            <Icon
              id={'left-caret'}
              width={16}
              height={16}
              className={`${
                open === 'products' ? 'rotate-90' : '-rotate-90'
              } text-sec-black transition-all duration-300`}
            />
          </button>
        </div>

        {open === 'products' && (
          <SubCategoryProducts
            subcategoryProducts={subcategoryProducts}
            subCategoryId={subCategoryId}
            categoryId={singleSubCategoryDetails?.data?.category?._id ?? ''}
            setSubcategoryProducts={setSubcategoryProducts}
            refetchSubCategory={() => {
              refetchSubCategoryDetails()
            }}
          />
        )}
      </div>

      {singleSubCategoryDetails?.data && (
        <AddSubCategoryModal
          open={modalOpen && modal === 'editSubCategoryModal'}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            handleModalClose()
          }}
          onSuccessCallback={() => {
            handleModalClose()
          }}
          mode="edit"
          categoryId={singleSubCategoryDetails.data.category._id}
          subCategory={singleSubCategoryDetails?.data}
        />
      )}

      {uploadedIcon ? (
        <ImageUploadPreviewModal
          image={uploadedIcon as File}
          open={modal === 'imageUploadPreviewModal' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            setUploadedIcon(undefined)
          }}
          onSuccessCallback={async (url) => {
            if (singleSubCategoryDetails?.data?._id) {
              await editSubCategory({
                id: subCategoryId,
                payload: {
                  icon: url,
                  name: singleSubCategoryDetails.data.name,
                  parentSubcategory:
                    singleSubCategoryDetails.data?.parentSubcategory?._id,
                  categoryId: singleSubCategoryDetails.data.category._id,
                  isActive: singleSubCategoryDetails.data.isActive,
                  ourPicks: productPicks.map((product) => product._id),
                },
              })
            }
          }}
        />
      ) : null}
    </div>
  )
}
